import { Image } from 'expo-image';
import type { StyleProp, ImageStyle } from 'react-native';

export interface CachedImageProps {
  /** Image URL to display. */
  uri: string;
  style?: StyleProp<ImageStyle> | undefined;
  /** Blurhash placeholder for instant preview while loading. */
  blurhash?: string | undefined;
  onError?: (() => void) | undefined;
  onLoad?: (() => void) | undefined;
}

/**
 * Thin wrapper around expo-image with app defaults:
 * - contentFit: cover
 * - cachePolicy: disk (persistent across restarts) — NATIVE only.
 * - transition: 200ms crossfade
 * - Optional blurhash placeholder
 *
 * Caching topology:
 *   Native: expo-image's disk cache stores image bytes across launches.
 *   Web: `cachePolicy="disk"` is a no-op (expo-image renders an `<img>` and
 *        falls through to the browser's HTTP cache). Within a session,
 *        place-photo URLs are deduped by `usePlacePhotoUrl` with
 *        `gcTime: Infinity` so component remounts reuse the resolved URL.
 *        ACROSS sessions on web, no persistent cache: Google's photoUri
 *        is a short-lived signed redirect (~1h), so persisting it would
 *        cause 403/404s when the URL expires. A real cross-session web
 *        image cache requires either a server-side proxy with stable
 *        URLs, or a service-worker byte cache — neither in scope today.
 */
export function CachedImage({ uri, style, blurhash, onError, onLoad }: CachedImageProps) {
  return (
    <Image
      source={{ uri }}
      style={style}
      contentFit="cover"
      cachePolicy="disk"
      {...(blurhash ? { placeholder: { blurhash } } : {})}
      transition={200}
      {...(onError ? { onError } : {})}
      {...(onLoad ? { onLoad } : {})}
    />
  );
}
